import { apiClientDeleteRequest } from 'ecommerce/lib/backendRequest';
import produce from 'utils/produce';
import httpRequest from 'utils/httpRequest';
import { apiClientGetRequest, apiClientPostRequest, apiClientPutRequest } from '../lib/backendRequest';
import SETTINGS from '../settings';

/**
 * Parse GID `{nodeId}:{treeId}`
 * @param {string} gid
 * @returns {string}
 */
const parseGid = gid => {
  const [id, treeId] = gid.split(':');

  return id;
};

/**
 * Format GID `{nodeId}:{treeId}`
 * @param {string} id
 * @param {number} treeId
 * @returns {string}
 */
const formatGid = (id, treeId) => {
  return [id, treeId].join(':');
};

const convertPersonToApi = (item, treeId) => {
  const data = {
    gid: formatGid(item.id, treeId),
    g: 'u',
    s: 'l',
    n: [],
    e: [],
    p: [],
    r: [],
  };

  // gender
  if (item.gender) {
    const transformGender = gender => {
      switch (gender) {
        case 'male':
          return 'm';
        case 'female':
          return 'f';
        default:
          return 'u';
      }
    };

    data.g = transformGender(item.gender);
  }

  // status
  if (item.status) {
    const transformStatus = status => {
      switch (status) {
        case 'living':
          return 'l';
        case 'deceased':
          return 'd';
        default:
          return 'u';
      }
    };

    data.s = transformStatus(item.status);
  }

  // names
  if (item.first) {
    data.n.push({ t: 'f', v: item.first });
  }
  if (item.last) {
    data.n.push({ t: 'l', v: item.last });
  }
  if (item.suffix) {
    data.n.push({ t: 's', v: item.suffix });
  }

  // events
  if (item.birthDate || item.birthPlace) {
    data.e.push({ t: 'b', d: item.birthDate, p: item.birthPlace });
  }
  if (item.deathDate || item.deathPlace) {
    data.e.push({ t: 'd', d: item.deathDate, p: item.deathPlace });
  }
  if (item.marriage) {
    Object.entries(item.marriage).forEach(([pid, event]) => {
      if (event.date || event.place) {
        data.e.push({ t: 'm', d: event.date, p: event.place, gid: formatGid(pid, treeId) });
      }
    });
  }

  // family
  if (item.mid) {
    data.r.push({ t: 'm', gid: formatGid(item.mid, treeId) }); // TODO id: ids[item.mid]
  }
  if (item.fid) {
    data.r.push({ t: 'f', gid: formatGid(item.fid, treeId) });
  }
  if (item.pids) {
    data.r.push(...item.pids.map(pid => ({ t: 'p', gid: formatGid(pid, treeId) })));
  }

  // photos
  if (item.photos) {
    data.p.push(...item.photos.map(photo => ({ t: photo.type, u: photo.url })));
  }

  return data;
};

export const convertPersonFromApi = item => {
  if (!item.id) {
    return undefined;
  }
  const { id: personId, tree_id: treeId, data } = item;
  const person = {
    personId,
    treeId,
    gid: data.gid,
    id: parseGid(data.gid),
    gender: 'unknown',
    status: 'living',
    marriage: {},
  };

  // gender
  if (data.g) {
    const transformGender = gender => {
      switch (gender) {
        case 'm':
          return 'male';
        case 'f':
          return 'female';
        default:
          return 'unknown';
      }
    };

    person.gender = transformGender(data.g);
  }

  // status
  if (data.s) {
    const transformStatus = status => {
      switch (status) {
        case 'l':
          return 'living';
        case 'd':
          return 'deceased';
        default:
          return 'unknown';
      }
    };

    person.status = transformStatus(data.s);
  }

  // names
  const names = data.n ?? [];
  const firstName = names.find(({ t }) => t === 'f');
  const lastName = names.find(({ t }) => t === 'l');
  const suffix = names.find(({ t }) => t === 's');

  if (firstName) {
    person.first = firstName.v;
  }
  if (lastName) {
    person.last = lastName.v;
  }
  if (suffix) {
    person.suffix = suffix.v;
  }

  // events
  const events = data.e ?? [];
  const birth = events.find(({ t }) => t === 'b');
  const death = events.find(({ t }) => t === 'd');
  const marriages = events.filter(({ t }) => t === 'm');
  if (birth) {
    if (birth.d) {
      person.birthDate = birth.d;
    }
    if (birth.p) {
      person.birthPlace = birth.p;
    }
  }
  if (death) {
    if (death.d) {
      person.deathDate = death.d;
    }
    if (death.p) {
      person.deathPlace = death.p;
    }
  }
  marriages.forEach(marriage => {
    const partnerId = parseGid(marriage.gid);
    if (partnerId) {
      person.marriage[partnerId] = {};
      if (marriage.d) {
        person.marriage[partnerId].date = marriage.d;
      }
      if (marriage.p) {
        person.marriage[partnerId].place = marriage.p;
      }
    }
  });

  // relations
  const relations = data.r ?? [];
  const mother = relations.find(({ t }) => t === 'm');
  const father = relations.find(({ t }) => t === 'f');
  const partners = relations.filter(({ t }) => t === 'p');
  if (mother) {
    person.mid = parseGid(mother.gid);
  }
  if (father) {
    person.fid = parseGid(father.gid);
  }
  person.pids = partners.map(partner => parseGid(partner.gid));

  // photos
  const photos = data.p ?? [];

  person.photos = photos
    .map(photo => ({ type: photo.t, url: photo.u }))
    .filter(photo => !!photo.url);
  const avatarPhoto = person.photos.find(({ type }) => type === 'a');
  if (avatarPhoto) {
    person.photo = avatarPhoto.url;
  }

  return person;
};

const treesApi = accessToken => ({
  /**
   * Get list of trees.
   * @returns {{ data: Array, error: object }}
   */
  async getTrees() {
    try {
      const response = await apiClientGetRequest('api/v1/trees', {
        headers: {
          Authorization: `Token ${accessToken}`,
        },
      });

      const json = await response.json();

      return {
        data: json.data,
      };
    } catch (error) {
      return { error };
    }
  },

  /**
   * Get one tree.
   * @param {string} treeId
   * @returns {{ data: object, error: object }}
   */
  async getTreePersons(treeId) {
    try {
      let json;
      // TODO for testing only
      if (treeId === 'dummy') {
        json = {
          data: [],
        };
      } else {
        const response = await apiClientGetRequest(`api/v1/trees/${treeId}/people`, {
          headers: {
            Authorization: `Token ${accessToken}`,
          },
        });

        json = await response.json();
      }

      return {
        data: json.data.map(convertPersonFromApi),
      };
    } catch (error) {
      return { error };
    }
  },

  /**
   * Create new tree
   * @param {string} caption
   * @returns {{ data: Array, error: object }}
   */
  async createTree(caption) {
    try {
      const response = await apiClientPostRequest(
        'api/v1/trees',
        {
          caption,
        },
        {
          Authorization: `Token ${accessToken}`,
        },
      );

      const json = await response.json();

      return {
        data: json.data,
      };
    } catch (error) {
      return { error };
    }
  },

  /**
   * Updates tree
   * @param {string} treeId
   * @param {string} caption
   * @returns {{ data: Array, error: object }}
   */
  async updateTree(treeId, caption) {
    try {
      const response = await apiClientPutRequest(
        `api/v1/trees/${treeId}`,
        {
          caption,
        },
        {
          Authorization: `Token ${accessToken}`,
        },
      );

      const json = await response.json();

      return {
        data: json.data,
      };
    } catch (error) {
      return { error };
    }
  },

  /**
   * Update tree
   * @param {string} treeId
   * @param {Array} persons
   * @returns {{ data: Array, error: object }}
   */
  async updateTreePersons(treeId, persons) {
    try {
      const response = await apiClientPostRequest(
        `api/v1/trees/${treeId}/people`,
        {
          persons: persons.map(({ personId, action, ...person }) => {
            if (personId) {
              if (action === 'delete') {
                return {
                  action: 'delete',
                  person: {
                    person_id: personId,
                  },
                };
              }
              return {
                action: 'update',
                person: {
                  person_id: personId,
                  data: convertPersonToApi(person, treeId),
                },
              };
            }
            return {
              action: 'create',
              person: {
                data: convertPersonToApi(person, treeId),
              },
            };
          }),
        },
        {
          Authorization: `Token ${accessToken}`,
        },
      );

      const json = await response.json();

      return {
        data: json.data.map(item => ({ ...item, person: convertPersonFromApi(item.person) })),
      };
    } catch (error) {
      return { error };
    }
  },

  async deletePerson(treeId, person, partners = []) {
    return this.updateTreePersons(treeId, [
      {
        action: 'delete',
        personId: person.personId,
      },
      ...partners.map(partner => ({
        action: 'update',
        ...partner,
        pids: (partner.pids ?? []).filter(pid => pid !== person.id),
        marriage: produce(partner.marrige ?? {}, draft => {
          delete draft[person.id];
        }),
      })),
    ]);
  },

  /**
   * Delete tree
   * @param {string} treeId
   * @returns {{ data: Array, error: object }}
   */
  async deleteTree(treeId) {
    try {
      const response = await apiClientDeleteRequest(`api/v1/trees/${treeId}`, {
        Authorization: `Token ${accessToken}`,
      });

      if (response.ok) {
        return { data: true };
      }
      return { error: response.status };
    } catch (e) {
      return { error: e.code };
    }
  },

  /**
   * Duplicate tree
   * @param {string} treeId
   * @param {string} newCaption
   * @returns {{ data: Array, error: object }}
   */
  async duplicateTree(treeId, newCaption) {
    const { data: newTreeData, error: newTreeError } = await this.createTree(newCaption);

    if (newTreeError) {
      return { error: newTreeError };
    }

    const { data: treePersons, error: treePersonsError } = await this.getTreePersons(treeId);

    if (treePersonsError) {
      return { error: treePersonsError };
    }

    const { data, error } = await this.updateTreePersons(
      newTreeData.id,
      treePersons.map(({ personId, ...person }) => person),
    );

    if (data) {
      return { data: newTreeData };
    }

    return { error };
  },

  async importGedcomFile(gedcomFile) {
    const body = new FormData();
    body.append('file', gedcomFile);
    body.append('auth_token', accessToken);

    if (!SETTINGS.GEDCOM_API_URL) {
      throw new Error('GEDCOM_API_URL value in the settings is not defined');
    }

    try {
      const response = await httpRequest(SETTINGS.GEDCOM_API_URL, {
        method: "POST",
        contentType: "multipart/form-data",
        body,
      });

      const json = await response.json();

      return {
        code: response.status,
        data: json,
      };
    } catch (error) {
      return { error };
    }
  }
});

export default treesApi;
